import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowUp, Square } from 'lucide-react';

import { sendAgentMessageSchema } from '@/app/agentConversation/_validators';

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/app/ui/form';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupText, InputGroupTextarea } from '@/app/ui/input-group';

/**
 * The message box. Enter sends, Shift+Enter starts a new line. While a reply is
 * being written the box is disabled and the send button becomes Stop.
 */
const Composer = ({ isRunning, onSend, onStop }) => {
  const form = useForm({ resolver: zodResolver(sendAgentMessageSchema), defaultValues: { text: '' } });
  const isBlank = !form.watch('text').trim();

  const onSubmit = (values) => {
    form.reset({ text: '' });
    onSend(values.text);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      if (!isRunning) form.handleSubmit(onSubmit)();
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="text"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="sr-only">Message</FormLabel>
              <InputGroup className="bg-background rounded-xl">
                <FormControl>
                  <InputGroupTextarea
                    {...field}
                    placeholder="Ask about your projects and estimates…"
                    className="max-h-40 min-h-0 overflow-auto px-3.5 pt-3 pb-0"
                    onKeyDown={handleKeyDown}
                    disabled={isRunning}
                    autoFocus
                    data-testid="agent-message-input"
                  />
                </FormControl>
                <InputGroupAddon align="block-end" className="justify-between pt-1 pr-2 pb-2 pl-3.5">
                  <InputGroupText className="text-xs">Shift + Enter for a new line</InputGroupText>
                  {isRunning ? (
                    <InputGroupButton variant="default" size="icon-sm" className="rounded-full" onClick={onStop} aria-label="Stop" data-testid="agent-stop-button">
                      <Square className="fill-current" />
                    </InputGroupButton>
                  ) : (
                    <InputGroupButton type="submit" variant="default" size="icon-sm" className="rounded-full" disabled={isBlank} aria-label="Send" data-testid="agent-send-button">
                      <ArrowUp />
                    </InputGroupButton>
                  )}
                </InputGroupAddon>
              </InputGroup>
              <FormMessage data-testid="agent-message-input-error" />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};

export default Composer;
